import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_URL } from '../../common/constants/AppConstants';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Define the type for profile settings
export interface ProfileSetting {
  id?: number;
  item_key: string;
  item_value: string;
  item_type: string;
  domain_name?: string;
  created_at?: string;
  updated_at?: string;
}

interface ProfileSettingsState {
  settings: ProfileSetting[];
  loading: boolean;
  error: string | null;
}

const initialState: ProfileSettingsState = {
  settings: [],
  loading: false,
  error: null,
};

// Create async thunk for fetching profile settings
export const fetchProfileSettings = createAsyncThunk(
  'profileSettings/fetchProfileSettings',
  async (itemType?: string) => {
    try {
      // Build the URL with optional query parameter
      let url = `${API_URL}/settings/getprofilesettings`;
      if (itemType) {
        url += `?itemType=${encodeURIComponent(itemType)}`;
      }

      // Get the access token for authorization
      const accessToken = await AsyncStorage.getItem('access-token');
      
      // Make the API call
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': accessToken ? `Bearer ${accessToken}` : '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile settings');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
);

// Create the profile settings slice
const profileSettingsSlice = createSlice({
  name: 'profileSettings',
  initialState,
  reducers: {
    // Add any additional reducers here if needed
    clearProfileSettings: (state) => {
      state.settings = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfileSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfileSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(fetchProfileSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch profile settings';
      });
  },
});

// Export actions and reducer
export const { clearProfileSettings } = profileSettingsSlice.actions;
export default profileSettingsSlice.reducer;
